import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLContainer } from '../../smartLinks/smlContainer/SMLContainer';
import { SMLPageHead } from '../components/SMLPageHead/SMLPageHead';
import { PageTabsInBody } from '../components/SMLPageHead/PageTabsInBody';
import { useCurrentNavScreen } from '../navigation/hooks/useCurrentNavScreen';
import { smlNavigationGroupMessages } from '../navigation/smlNavigationMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from './Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PromoteContainerProps {
  title: string;
  subtitle?: string;
  /** The screen's own buttons, shown on the right of the page head. */
  actions?: React.ReactNode;
  /** The crumbs after the navigation group. Default: the title. */
  crumbs?: string[];
  /** The page tabs under the head (navigation v2 `SMLSectionTabs`). */
  tabs?: React.ReactNode;
  /** The back link of a page under a menu item (navigation v2 `SMLBackLink`). */
  back?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The shell every Promote screen sits in: the page head, the page tabs and the body. The crumb
 * names the navigation group of the page: Social and Fans sit in Your music (Insights), the
 * others in Promote.
 */
const PromoteContainerComponent: React.FC<PromoteContainerProps> = ({
  title,
  subtitle,
  actions,
  crumbs,
  tabs,
  back,
  children,
}) => {
  const intl = useIntl();
  // The crumb names the navigation group of the page on screen (design a1). A page outside the
  // groups (Channels, now Settings → Connections) falls back to Promote.
  const { group } = useCurrentNavScreen();
  const breadcrumbs = useMemo(
    () => [
      { name: intl.formatMessage(group?.label ?? smlNavigationGroupMessages.promoteName) },
      ...(crumbs ?? [title]).map((name) => ({ name })),
    ],
    [intl, group, title, crumbs]
  );

  return (
    <SMLContainer
      header={
        <SMLPageHead breadcrumbs={breadcrumbs} title={title} subtitle={subtitle} back={back} tabs={tabs}>
          {actions}
        </SMLPageHead>
      }
    >
      <div className={styles.panelStack}>
        {tabs !== undefined ? <PageTabsInBody>{tabs}</PageTabsInBody> : null}
        {children}
      </div>
    </SMLContainer>
  );
};

export const PromoteContainer = TypedMemo(PromoteContainerComponent);
